import _ from 'lodash'
import logger from '@adc/logger'
import instrument from '@adc/instrument'
import { getNullApplicationNotifications } from '@adc/ui-adc'
import * as saveSearchProvider from '../providers/search'
import {
  SAVED_SEARCH_ADDED,
  SAVED_SEARCH_LOOKUP,
  SAVED_SEARCH_OPEN_MODAL,
  SAVED_SEARCH_CLOSE_MODAL,
} from '../constants/actionTypes'
import {
  trackSaveSearch,
  ONE_CLICK_SAVE_SEARCH_SUCCESS,
  ONE_CLICK_SAVE_SEARCH_UPDATE_NAME,
  ONE_CLICK_SAVE_SEARCH_REMOVE,
  ONE_CLICK_SAVE_SEARCH_CLOSE,
} from '../services/analytics'
import { getDefaultSaveSearchNameFromFilters } from '../converters/filters'
import { fetchUserSavedSearches } from './fetchUserSavedSearches'

/**
 * Internal Actions
 */
const updateSavedSearchLookup = value => ({
  type: SAVED_SEARCH_LOOKUP,
  value,
})

const getSaveSearchParametersFromBlueprintData = blueprintData => {
  const { currentFilters, listingCompatabilityFilters } = blueprintData

  const defaultSavedSearchName = _.trim(
    getDefaultSaveSearchNameFromFilters(currentFilters)
  )

  const apiValue = {
    ...listingCompatabilityFilters,
    offset: '0',
    auction_type: 'residential',
    use_resi_collection: 'y',
    show_sold_assets: 'y',
  }

  return [defaultSavedSearchName, apiValue]
}

/**
 * Exported Actions
 */

export const openLightbox = ({
  authType,
  notifications = getNullApplicationNotifications(),
  blueprintData,
} = {}) => {
  return async dispatch => {
    try {
      await dispatch(saveSearch({ notifications, blueprintData }))

      dispatch({
        type: SAVED_SEARCH_OPEN_MODAL,
        authType,
        data: {},
      })
    } catch (err) {
      logger.error('Error Saving Search', err)
      notifications.overlayBanner.open({
        title: 'Unable to save search',
        role: 'error',
      })
    }
  }
}

export const closeLightbox = ({
  savedSearchToDelete = null,
  notifications = getNullApplicationNotifications(),
} = {}) => {
  return (dispatch, getState) => {
    trackSaveSearch({
      event: ONE_CLICK_SAVE_SEARCH_CLOSE,
      user: getState().user,
    })

    if (getState().savedSearch.authType === 'signup') {
      notifications.overlayBanner.open({
        title: 'Thank you for signing up.',
      })
    }

    if (savedSearchToDelete) {
      // no await for this as it doesn't change whether we close the modal or not
      dispatch(removeSavedSearch({ savedSearchToDelete, notifications }))
    }

    dispatch({
      type: SAVED_SEARCH_CLOSE_MODAL,
      data: {},
    })
  }
}

export const saveSearch = ({
  searchData = {},
  blueprintData,
  notifications = getNullApplicationNotifications(),
} = {}) => {
  return (dispatch, getState) => {
    const [defaultSearchName, apiValue] =
      getSaveSearchParametersFromBlueprintData(blueprintData)

    const body = {
      parameters: apiValue,
      saved_search_name: searchData.saveSearchName || defaultSearchName,
    }

    return saveSearchProvider
      .saveSearch(body)
      .then(res => {
        const fromSignup = getState().savedSearch.authType === 'signup'
        const searchLookupValue = {
          isSearchSaved: true,
          savedSearchName: searchData.saveSearchName,
        }

        instrument.increment('search_save', 1)
        trackSaveSearch({
          event: ONE_CLICK_SAVE_SEARCH_SUCCESS,
          user: getState().user,
        })
        dispatch({ type: SAVED_SEARCH_ADDED, value: res?.saved_search_id })
        dispatch(updateSavedSearchLookup(searchLookupValue))
        // TODO: We can potentially stop this by appending directly when we add the saved search on L91 above.
        dispatch(fetchUserSavedSearches())
        notifications.overlayBanner.open({
          title: fromSignup
            ? 'Thank you for signing up. Your search has been saved.'
            : 'Save Successful',
          message: fromSignup
            ? 'You can also register to bid, get property alerts and more.'
            : 'Your search criteria has been saved and we will notify you when new properties have been listed. Access all your notifications from your Dashboard.',
        })
      })
      .catch(err => {
        throw err
      })
  }
}

export const updateSavedSearch = ({
  newSearchName = null,
  notifications = getNullApplicationNotifications(),
  blueprintData,
}) => {
  return (dispatch, getState) => {
    const [, apiValue] = getSaveSearchParametersFromBlueprintData(blueprintData)
    const body = {
      parameters: apiValue,
      saved_search_id: getState().savedSearch.mostRecentSavedSearchId,
      saved_search_name: newSearchName,
    }

    return saveSearchProvider
      .updateSearch(body)
      .then(res => {
        // fetch saved searches to show new name
        dispatch(fetchUserSavedSearches())
        dispatch(
          updateSavedSearchLookup({
            savedSearchName: newSearchName,
          })
        )

        trackSaveSearch({
          event: ONE_CLICK_SAVE_SEARCH_UPDATE_NAME,
          user: getState().user,
        })

        notifications.overlayBanner.open({
          title: 'Search Name Updated',
        })

        dispatch(closeLightbox({ notifications }))
      })
      .catch(err => {
        throw err
      })
  }
}

export const savedSearchLookupForBlueprintSearch = apiFilters => {
  return (dispatch, getState) => {
    /* eslint-disable camelcase */
    const { logged_in, saved_searches } = getState().user

    if (!logged_in) {
      return
    }

    // TODO: wth is going on here ?
    const keysToSkipChecking = ['offset', 'limit', 'coords']

    const savedSearchParameters = _.omit(apiFilters, keysToSkipChecking)

    ;['nearby_search_radius', 'version'].forEach(key => {
      if (savedSearchParameters[key]) {
        savedSearchParameters[key] = String(savedSearchParameters[key])
      }
    })

    const apiValue = {
      ...savedSearchParameters,
      // TODO: lines below seem useless. Keeping for for backwards compat, because current saved search
      // actions adds them to the payload when saving search
      auction_type: 'residential',
      use_resi_collection: 'y',
      show_sold_assets: 'y',
    }

    const _savedSearch = _.find(saved_searches, o =>
      _.isEqual(_.omit(o.parameters, keysToSkipChecking), apiValue)
    )

    const isSearchSaved = !!_savedSearch

    return dispatch(
      updateSavedSearchLookup({
        isSearchSaved,
        ...(isSearchSaved
          ? { savedSearchName: _savedSearch.saved_search_name }
          : {}),
      })
    )
  }
}

export const removeSavedSearch = ({
  savedSearchToDelete,
  notifications = getNullApplicationNotifications(),
}) => {
  return (dispatch, getState) => {
    const body = {
      saved_search_id: savedSearchToDelete,
    }

    return saveSearchProvider
      .deleteSearch(body)
      .then(res => {
        if (res) {
          // res should be false if delete fails but returns
          instrument.increment('search_delete', 1)
          trackSaveSearch({
            event: ONE_CLICK_SAVE_SEARCH_REMOVE,
            user: getState().user,
          })
          notifications.overlayBanner.open({
            title: 'Search Deleted',
          })

          dispatch(
            updateSavedSearchLookup({
              isSearchSaved: false,
            })
          )
        }
      })
      .catch(err => {
        throw err
      })
  }
}
